(() => {
  // Tracks which fee variants were already added per add-to-cart event, so several add-ons can react to one event.
  const handledAddEvents = new WeakMap();
  // Cart requests are queued so add-ons don't race each other.
  let cartQueue = Promise.resolve();

  if (customElements.get('installation-fees')) return;

  class InstallationFees extends HTMLElement {
    constructor() {
      super();
      this.inFlight = false;
      this.checkoutStates = new Map();
      this.onCheckboxChange = this.onCheckboxChange.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.syncDynamicCheckout = this.syncDynamicCheckout.bind(this);
    }

    get checkbox() {
      return this.querySelector('.installation-fees__checkbox');
    }

    get errorMessage() {
      return this.querySelector('.installation-fees__error');
    }

    connectedCallback() {
      if (!this.checkbox && !this.isRequired) return;

      this.productBlock = this.closest('.product__block--buy_buttons');
      this.checkbox?.addEventListener('change', this.onCheckboxChange);
      document.addEventListener('product-ajax:added', this.onProductAdded);

      if (this.productBlock) {
        this.checkoutObserver = new MutationObserver(this.syncDynamicCheckout);
        this.checkoutObserver.observe(this.productBlock, { childList: true, subtree: true });
      }

      this.syncDynamicCheckout();
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this.onCheckboxChange);
      document.removeEventListener('product-ajax:added', this.onProductAdded);
      this.checkoutObserver?.disconnect();
      this.restoreDynamicCheckout();
    }

    get isRequired() {
      return this.hasAttribute('required');
    }

    // Required add-ons have no checkbox and are always selected.
    get isSelected() {
      return this.isRequired || Boolean(this.checkbox?.checked);
    }

    onCheckboxChange() {
      this.hideError();
      this.syncDynamicCheckout();
    }

    syncDynamicCheckout() {
      const checkoutSurface = this.productBlock?.querySelector('.shopify-payment-button');

      if (!this.isSelected) {
        this.restoreDynamicCheckout();
        return;
      }

      if (!checkoutSurface) return;

      if (!this.checkoutStates.has(checkoutSurface)) {
        this.checkoutStates.set(checkoutSurface, {
          hidden: checkoutSurface.hidden,
          ariaHidden: checkoutSurface.getAttribute('aria-hidden'),
          inert: checkoutSurface.hasAttribute('inert'),
        });
      }

      checkoutSurface.hidden = true;
      checkoutSurface.setAttribute('aria-hidden', 'true');
      checkoutSurface.setAttribute('inert', '');
    }

    restoreDynamicCheckout() {
      this.checkoutStates.forEach((state, checkoutSurface) => {
        checkoutSurface.hidden = state.hidden;

        if (state.ariaHidden === null) {
          checkoutSurface.removeAttribute('aria-hidden');
        } else {
          checkoutSurface.setAttribute('aria-hidden', state.ariaHidden);
        }

        checkoutSurface.toggleAttribute('inert', state.inert);
      });
      this.checkoutStates.clear();
    }

    async onProductAdded(event) {
      if (!this.isSelected || this.inFlight) return;

      const detail = event.detail || {};
      const product = detail.product;
      const eventProductId = product?.product_id ?? product?.productId;
      if (!eventProductId || String(eventProductId) !== String(this.dataset.productId)) return;

      const variantId = this.dataset.feeVariantId;
      if (!variantId) return;

      const handledVariants = handledAddEvents.get(event) || new Set();
      if (handledVariants.has(variantId)) return;
      handledVariants.add(variantId);
      handledAddEvents.set(event, handledVariants);

      this.inFlight = true;
      if (this.checkbox) this.checkbox.disabled = true;
      this.hideError();

      const eventQuantity = Number.parseInt(product.quantity ?? detail.quantity, 10);
      const quantity = Math.max(1, Number.isFinite(eventQuantity) ? eventQuantity : 1);

      const previousRequest = cartQueue;
      let releaseQueue;
      cartQueue = new Promise((resolve) => (releaseQueue = resolve));

      try {
        await previousRequest;
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        const baseConfig = FoxTheme.utils.fetchConfig('javascript');
        const response = await fetch(FoxTheme.routes.cart_add_url, {
          ...baseConfig,
          method: 'POST',
          headers: {
            ...baseConfig.headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const parsedState = await response.json();

        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || 'Installation fee could not be added');
        }

        try {
          const cartResponse = await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig('json', 'GET'));
          const cartJson = await cartResponse.json();
          cartJson.sections = parsedState.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        } catch (error) {
          console.error('Installation fee was added, but the cart could not be refreshed:', error);
        }
      } catch (error) {
        console.error('Error adding installation fee to cart:', error);
        if (this.errorMessage) this.errorMessage.hidden = false;
      } finally {
        releaseQueue();
        this.inFlight = false;
        if (this.checkbox) this.checkbox.disabled = false;
      }
    }

    hideError() {
      if (this.errorMessage) this.errorMessage.hidden = true;
    }
  }

  customElements.define('installation-fees', InstallationFees);
})();
